import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { motion } from 'framer-motion';
import { 
  CreditCard, Tag, Check, AlertCircle, Loader2, 
  Shield, Clock, ArrowLeft, Sparkles 
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

const CheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();

  const plan = searchParams.get('plan');
  const billing = searchParams.get('billing') || 'monthly';
  const currency = searchParams.get('currency') || 'INR';

  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [isVerifyingCoupon, setIsVerifyingCoupon] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const planDetails = {
    starter: {
      name: 'Starter',
      monthly: { INR: 299, USD: 3.99 },
      yearly: { INR: 2990, USD: 39.99 },
      features: ['10,000 requests/month', 'Up to 5 API keys', 'Email support', 'Advanced analytics']
    },
    professional: {
      name: 'Professional',
      monthly: { INR: 999, USD: 12.99 },
      yearly: { INR: 9990, USD: 129.99 },
      features: ['100,000 requests/month', '5+ API keys', 'Priority support', 'Custom rate limits']
    }
  };

  const selectedPlan = planDetails[plan];
  const baseAmount = selectedPlan?.[billing]?.[currency] || 0;
  const discount = couponApplied?.discount || 0;
  const finalAmount = baseAmount - discount;

  useEffect(() => {
    if (!isLoaded) return;
    
    if (!user) {
      toast.error('Please sign in to continue');
      navigate('/sign-in');
      return;
    }

    if (!plan || !selectedPlan) {
      toast.error('Invalid plan selected');
      navigate('/pricing');
    }
  }, [isLoaded, user, plan, selectedPlan, navigate]);

  const verifyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setIsVerifyingCoupon(true);
    setCouponError('');
    setCouponApplied(null);

    try {
      const response = await api.post('/api/payment/verify-coupon', {
        code: couponCode.toUpperCase(),
        plan,
        amount: baseAmount
      });

      if (response.data.valid) {
        setCouponApplied(response.data);
        toast.success(`Coupon applied! You save ${currency === 'INR' ? '₹' : '$'}${response.data.discount}`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Invalid coupon code';
      setCouponError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsVerifyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setCouponApplied(null);
    setCouponError('');
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (isProcessing) return;

    setIsProcessing(true);

    try {
      // Load Razorpay script
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error('Failed to load payment gateway. Please try again.');
        setIsProcessing(false);
        return;
      }

      // Create order
      const orderResponse = await api.post('/api/payment/create-order', {
        plan,
        billingPeriod: billing,
        currency,
        couponCode: couponApplied ? couponCode : null
      });

      const { orderId, amount, keyId } = orderResponse.data;

      // Configure Razorpay options
      const options = {
        key: keyId,
        amount: amount * 100, // Convert to paise/cents
        currency: currency,
        name: 'NSFW API',
        description: `${selectedPlan.name} Plan - ${billing}`,
        order_id: orderId,
        prefill: {
          name: user.fullName || user.firstName || '',
          email: user.primaryEmailAddress?.emailAddress || '',
        },
        theme: {
          color: '#E31B54'
        },
        handler: async function (response) {
          try {
            // Verify payment
            const verifyResponse = await api.post('/api/payment/verify-payment', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan,
              billingPeriod: billing,
              currency,
              couponCode: couponApplied ? couponCode : null
            });

            if (verifyResponse.data.success) {
              toast.success('Payment successful! Redirecting to dashboard...');
              setTimeout(() => {
                navigate('/dashboard');
              }, 2000);
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error('Payment verification failed. Please contact support.');
            setIsProcessing(false);
          }
        },
        modal: {
          ondismiss: function() {
            setIsProcessing(false);
            toast.error('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.error || 'Failed to process payment');
      setIsProcessing(false);
    }
  };

  if (!isLoaded || !user || !selectedPlan) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16 bg-gradient-to-b from-gray-50 to-white dark:from-dark-950 dark:to-dark-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back button */}
        <Link
          to="/pricing"
          className="inline-flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Pricing</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Plan Summary */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                  <Shield className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{selectedPlan.name} Plan</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Billed {billing}
                  </p>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                {selectedPlan.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-200 dark:border-dark-800 pt-6 space-y-3">
                <div className="flex justify-between text-gray-700 dark:text-gray-300">
                  <span>Subtotal</span>
                  <span className="font-medium">
                    {currency === 'INR' ? '₹' : '$'}{baseAmount}
                  </span>
                </div>

                {couponApplied && (
                  <div className="flex justify-between text-green-600 dark:text-green-400">
                    <span className="flex items-center space-x-2">
                      <Tag className="w-4 h-4" />
                      <span>Discount ({couponCode})</span>
                    </span>
                    <span className="font-medium">
                      -{currency === 'INR' ? '₹' : '$'}{discount}
                    </span>
                  </div>
                )}

                <div className="flex justify-between text-xl font-bold border-t border-gray-200 dark:border-dark-800 pt-3">
                  <span>Total</span>
                  <span className="gradient-text">
                    {currency === 'INR' ? '₹' : '$'}{finalAmount}
                  </span>
                </div>
              </div>
            </div>

            {/* Trust badges */}
            <div className="card">
              <div className="flex items-center justify-around text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center space-x-2">
                  <Shield className="w-4 h-4" />
                  <span>Secure</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4" />
                  <span>Instant Access</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Sparkles className="w-4 h-4" />
                  <span>Cancel Anytime</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Payment Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            {/* Coupon Code */}
            <div className="card">
              <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                <Tag className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                <span>Have a coupon code?</span>
              </h3>

              {!couponApplied ? (
                <div className="space-y-3">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value.toUpperCase());
                        setCouponError('');
                      }}
                      className="input flex-1"
                      disabled={isVerifyingCoupon}
                    />
                    <button
                      onClick={verifyCoupon}
                      disabled={isVerifyingCoupon || !couponCode.trim()}
                      className="btn-outline whitespace-nowrap"
                    >
                      {isVerifyingCoupon ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        'Apply'
                      )}
                    </button>
                  </div>

                  {couponError && (
                    <div className="flex items-start space-x-2 text-sm text-red-600 dark:text-red-400">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{couponError}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                      <span className="font-semibold text-green-900 dark:text-green-100">
                        {couponCode}
                      </span>
                    </div>
                    <button
                      onClick={removeCoupon}
                      className="text-sm text-green-600 dark:text-green-400 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    {couponApplied.description || 'Coupon applied successfully'}
                  </p>
                </div>
              )}
            </div>

            {/* Payment Button */}
            <div className="card">
              <button
                onClick={handlePayment}
                disabled={isProcessing}
                className="btn-primary w-full flex items-center justify-center space-x-2 text-lg py-4"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    <span>Pay {currency === 'INR' ? '₹' : '$'}{finalAmount}</span>
                  </>
                )}
              </button>

              <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-4">
                Secured by Razorpay. Your payment information is encrypted and secure.
              </p>
            </div>

            {/* Info */}
            <div className="card bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900 dark:text-blue-100">
                  <p className="font-semibold mb-1">7-Day Money Back Guarantee</p>
                  <p className="text-blue-700 dark:text-blue-300">
                    Not satisfied? Get a full refund within 7 days, no questions asked.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
